<template>
  <div class="pack-store-item-block-container">
    <div class="pack-store-item-block">
      <div class="pack-store-item-icon-container">
        <div class="pack-store-item-icon-background">
          <div class="pack-store-item-icon">
            <img
              :src="`${imgDomain}/${imageUrl}`"
              :alt="packName"
              class="pack-icon-image"
            />
          </div>
        </div>
      </div>
      <div class="pack-store-item-title" :title="packName">{{ packName }}</div>
      <div class="pack-store-item-separator"></div>
      <div class="pack-store-item-description" :title="description">
        {{ description }}
      </div>
      <button class="pack-products-toggle" @click="toggleProducts">
        {{ isExpanded ? "Hide items" : "Show items" }}
      </button>
      <div class="pack-store-item-separator"></div>
      <div class="pack-store-item-price-container">
        <span class="pack-store-item-price-number">{{ formattedPrice }}</span>
        <span class="pack-store-item-price-currency">{{ currency }}</span>
      </div>
      <button
        class="pack-store-item-buy-button"
        :disabled="isLoading"
        @click="handlePurchase"
      >
        <span v-if="!isAdded && !isLoading">{{ $t("BUY") }}</span>
        <span v-else-if="isLoading">{{ $t("PROCESSING...") }}</span>
        <span v-else class="added">{{ $t("ADDED") }}</span>
      </button>
    </div>
    <div class="pack-products" v-if="isExpanded">
      <div
        class="pack-products-item"
        v-for="item in packProducts"
        :key="item.id"
      >
        <img
          :src="`${imgDomain}/${item.image_url}`"
          :alt="item.title"
          class="pack-products-icon-image"
        />
        <div class="pack-products-item__title">{{ item.title }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

export default {
  name: "PackStoreItem",
  props: {
    packId: {
      type: [String, Number],
      required: true,
    },
    packName: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    price: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      default: "EUR",
    },
    imageUrl: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    packProducts: {
      type: Array,
    },
  },
  emits: ["purchase"],
  setup(props, { emit }) {
    const imgDomain = process.env.VUE_APP_STATIC_DOMAIN;

    const formattedPrice = computed(() => {
      return props.price.toFixed(2);
    });

    const isAdded = ref(false);

    const isExpanded = ref(false);

    const toggleProducts = () => {
      isExpanded.value = !isExpanded.value;
    };

    const handlePurchase = () => {
      if (!props.isLoading && !props.isDisabled) {
        emit("purchase", {
          packId: props.packId,
          packName: props.packName,
          price: props.price,
          currency: props.currency,
        });

        isAdded.value = true;
        setTimeout(() => {
          isAdded.value = false;
        }, 2000);
      }
    };

    return {
      imgDomain,
      formattedPrice,
      handlePurchase,
      isAdded,
      isExpanded,
      toggleProducts,
    };
  },
};
</script>

<style scoped>
.pack-store-item-block {
  background: #f9f9f9;
  border-radius: 20px;
  padding: 16px 40px 16px 16px;
  height: 136px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  gap: 0;
}

.pack-store-item-icon-container {
  width: 104px;
  height: 104px;
  position: relative;
  flex-shrink: 0;
}

.pack-store-item-icon-background {
  width: 104px;
  height: 104px;
  background: linear-gradient(90deg, #f1f1f1 0%, #f9f9f9 50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pack-store-item-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pack-icon-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.pack-store-item-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: #22a8fd;
  margin-left: 18px;
  width: 130px;
  flex-shrink: 0;
  word-break: break-word;
}

.pack-store-item-separator {
  width: 1px;
  height: 104px;
  background: #d9d9d9;
  margin: 0 28px;
  flex-shrink: 0;
}

.pack-store-item-description {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  color: #6e7982;
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pack-store-item-price-container {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin: 0 18px;
  flex-shrink: 0;
}

.pack-store-item-price-number {
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #011525;
}

.pack-store-item-price-currency {
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #22a8fd;
}

.pack-store-item-buy-button {
  width: 182px;
  height: 46px;
  background: transparent;
  border: 1px solid #22a8fd;
  border-radius: 26px;
  font-family: "Open Sans", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 19px;
  text-transform: uppercase;
  color: #011525;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
}

.pack-store-item-buy-button:hover:not(:disabled) {
  background: #22a8fd;
  color: white;
}

.pack-store-item-buy-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pack-store-item-buy-button:active:not(:disabled) {
  transform: scale(0.98);
}

.added {
  color: #4caf50;
}

.pack-products {
  display: flex;
  flex-wrap: wrap;
  padding-top: 16px;
}
.pack-products-item {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 25%;
}
.pack-products-icon-image {
  width: 100px;
}
.pack-products-toggle {
  background: transparent;
}

@media (max-width: 1200px) {
  .pack-store-item-block {
    padding: 16px 20px 16px 16px;
  }
  .pack-store-item-title {
    width: 150px;
  }
  .pack-products-item {
    width: 50%;
  }
}

@media (max-width: 768px) {
  .pack-store-item-block {
    height: auto;
    min-height: 136px;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
    gap: 16px;
  }
  .pack-store-item-icon-container {
    align-self: center;
    width: 80px;
    height: 80px;
  }
  .pack-store-item-icon-background {
    width: 80px;
    height: 80px;
  }
  .pack-store-item-icon {
    width: 50px;
    height: 50px;
  }
  .pack-store-item-title {
    margin: 0;
    text-align: center;
    font-size: 20px;
    line-height: 24px;
    width: 100%;
  }
  .pack-store-item-separator {
    height: 1px;
    width: 100%;
    margin: 0;
  }
  .pack-store-item-description {
    width: 100%;
    margin: 0;
    text-align: center;
    font-size: 16px;
    line-height: 20px;
  }
  .pack-store-item-price-container {
    justify-content: center;
    margin: 0;
  }
  .pack-store-item-price-number,
  .pack-store-item-price-currency {
    font-size: 16px;
    line-height: 20px;
  }
  .pack-store-item-buy-button {
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .pack-store-item-block {
    padding: 16px;
  }
  .pack-store-item-description {
    font-size: 14px;
    line-height: 18px;
  }
  .pack-store-item-buy-button {
    max-width: none;
  }

  .pack-products-item {
    width: 100%;
  }
}
</style>
